import { BooleanInput } from '@angular/cdk/coercion';
import {
  booleanAttribute,
  Component,
  computed,
  forwardRef,
  input,
  model,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideCalendar } from '@ng-icons/lucide';
import { type ChangeFn, type TouchFn } from '@spartan-ng/brain/forms';
import { BrnPopover, BrnPopoverContent, BrnPopoverTrigger } from '@spartan-ng/brain/popover';
import { HlmCalendarComponent } from '@spartan-ng/ui-calendar-helm';
import { EXPOSES_STATE_TOKEN } from '@spartan-ng/brain/core';
import { hlm } from '@spartan-ng/helm/utils';
import { HlmIconComponent } from '@spartan-ng/ui-icon-helm';
import { HlmInputDirective } from '@spartan-ng/ui-input-helm';
import { HlmPopoverContentDirective } from '@spartan-ng/ui-popover-helm';
import type { ClassValue } from 'clsx';
import { injectHlmDatePickerConfig } from './hlm-date-picker.token';

/**
 * This injection token is required for the directive to function correctly,
 * especially when used inside components like HlmPopoverContentDirective.
 */
export const HLM_EXPOSES_STATE_TOKEN = {
  provide: EXPOSES_STATE_TOKEN,
  useValue: {},
};

export const HLM_DATE_PICKER_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => HlmDatePickerComponent),
  multi: true,
};

@Component({
  selector: 'hlm-date-picker',
  standalone: true,
  imports: [
    HlmIconComponent,
    BrnPopover,
    BrnPopoverTrigger,
    BrnPopoverContent,
    HlmPopoverContentDirective,
    HlmCalendarComponent,
    HlmInputDirective,
  ],
  providers: [HLM_DATE_PICKER_VALUE_ACCESSOR, provideIcons({ lucideCalendar }), HLM_EXPOSES_STATE_TOKEN],
  template: `
    <brn-popover sideOffset="5" closeDelay="100">
      <div hlmInput [class.focus]="isOpen()" [class]="'flex overflow-hidden ' + userClass()">
        <button
          type="button"
          [class]="_computedClass()"
          [disabled]="state().disabled()"
          brnPopoverTrigger
          [style.color]="iconColor()"
        >
          <hlm-icon
            hlm
            size="unset"
            name="lucideCalendar"
            displayStyle="flex"
            class="calendar-icon flex items-center justify-center text-inherit pl-3"
            [class.labelActive]="isOpen()"
            [color]="iconColor()"
          />

          @if (formattedDate(); as formattedDate) {
          <div [class.labelActive]="isOpen()">
            {{ formattedDate }}
          </div>
          } @else {
          <ng-content />
          }
        </button>
      </div>

      <div id="web-app" *brnPopoverContent="let ctx" style="background-color: transparent;">
        <div hlmPopoverContent [class]="'w-auto ' + userClass()" style="padding: 0 !important;">
          <hlm-calendar
            [calendarClass]="userClass()"
            [date]="date()"
            [min]="min()"
            [max]="max()"
            [disabled]="state().disabled()"
            (dateChange)="_handleChange($event)"
          />
        </div>
      </div>
    </brn-popover>
  `,
  host: {
    class: 'block',
  },
  styles: `

  .calendar-icon {
      width: 1em;
      height: 1em;
  }

  `,
})
export class HlmDatePickerComponent<T> {
  @ViewChild(BrnPopover) popover!: BrnPopover;
  public readonly autoCloseOnSelect = input<boolean>(true);
  public readonly isOpen = input<boolean>(false);
  public readonly iconColor = input<string | undefined>(undefined);
  private readonly _config = injectHlmDatePickerConfig<T>();

  public readonly userClass = input<ClassValue>('');
  protected readonly _computedClass = computed(() =>
    hlm(
      'inline-flex w-full text-inherit relative z-[2] border-none bg-transparent items-center gap-2 whitespace-nowrap rounded-md ring-offset-background transition-colors border border-input h-auto px-0 py-0 justify-start',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'disabled:pointer-events-none disabled:opacity-50',
      '[&_ng-icon]:pointer-events-none [&_ng-icon]:shrink-0',
      !this.date() ? 'text-muted-foreground' : '',
      this.userClass(),
    ),
  );

  /** The minimum date that can be selected.*/
  public readonly min = input<T>();

  /** The maximum date that can be selected. */
  public readonly max = input<T>();

  /** Determine if the date picker is disabled. */
  public readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  /** The selected value. */
  public readonly date = model<T>();

  protected readonly state = computed(() => ({
    disabled: signal(this.disabled()),
  }));

  /** Defines how the date should be displayed in the UI.  */
  public readonly formatDate = input<(date: T) => string>(this._config.formatDate);

  /** Defines how the date should be transformed before saving to model/form. */
  public readonly transformDate = input<(date: T) => T>(this._config.transformDate);

  protected readonly formattedDate = computed(() => {
    const date = this.date();
    return date ? this.formatDate()(date) : undefined;
  });

  public readonly changed = output<T>();

  protected _onChange?: ChangeFn<T>;
  protected _onTouched?: TouchFn;

  protected _handleChange(value: T) {
    if (this.state().disabled()) return;
    const transformedDate = this.transformDate()(value);

    this.date.set(transformedDate);
    this._onChange?.(transformedDate);
    this.changed.emit(transformedDate);

    // Auto-close the popover if enabled
    if (this.autoCloseOnSelect() && this.popover?.close) {
      this.popover.close(true, 100);
    }
  }

  /** CONTROL VALUE ACCESSOR */
  writeValue(value: T | null): void {
    // optional FormControl is initialized with null value
    if (value === null) return;

    this.date.set(this.transformDate()(value));
  }

  registerOnChange(fn: ChangeFn<T>): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: TouchFn): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.state().disabled.set(isDisabled);
  }
}
